import { ChangeEvent, Dispatch, SetStateAction, useRef, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { CustomForm } from '@/components/Formik/CustomForm';
import { CustomIOSSwitch } from '@/components/Formik/CustomSwitch';
import { CustomTextField } from '@/components/Formik/CustomTextField';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { IPortfolioItem } from '@/types/api/companies';
import {
  ALLOWED_IMAGE_FILE_TYPES,
  MAXIMUM_IMG_SIZE_ALLOWED_PORTFOLIO,
  MAX_IMAGES_PORTFOLIO,
  ROUTER_PAGES,
} from '@/utils/constants';
import { removeClassFromParent } from '@/utils/dom';
import { handleErrorAsync } from '@/utils/handlers';
import { modalAddPortfolioSchema } from '@/validators/modalAddPortfolio';
import {
  AddToPhotosOutlined,
  DeleteForever,
  ImageOutlined,
  InfoOutlined,
  KeyboardBackspace,
  PushPinOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@mui/material';

interface IAddPortfolioProps {
  isEditPortfolioItem?: IPortfolioItem;
  setValuesPortfolioItem: (value: IPortfolioItem | undefined) => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  companyId: number;
}

export const AddPortfolio = ({
  isEditPortfolioItem,
  setValuesPortfolioItem,
  isOpen,
  setIsOpen,
  companyId,
}: IAddPortfolioProps) => {
  const router = useRouter();
  const { portfolio_slug, company_slug } = router.query;
  const { setIsLoading } = useLoading();
  const [images, setImages] = useState<File[]>([]);
  const imagePortfolioInput = useRef<HTMLInputElement | null>(null);
  const portfolioImageInAnalysis = isEditPortfolioItem
    ? isEditPortfolioItem.photos
    : images;

  const resetInput = (input: HTMLInputElement | null) => {
    if (input) {
      input.value = '';
    }
  };

  const handleAddImage = (event: ChangeEvent<HTMLInputElement>) => {
    const newImages = [...images];
    let extraImagesCount = 0;
    if (event.target.files) {
      for (let i = 0; i < event.target.files.length; i++) {
        const file = event.target.files[i];
        resetInput(event.target);
        if (file.size > MAXIMUM_IMG_SIZE_ALLOWED_PORTFOLIO) {
          Toast('error', 'Tamanho máximo de arquivo excedido (6 mb)');
          return;
        }

        if (!ALLOWED_IMAGE_FILE_TYPES.includes(file.type)) {
          Toast(
            'error',
            'Formato não suportado! (São permitidos: PNG, JPG, JPEG)'
          );
          return;
        }

        if (newImages.length < MAX_IMAGES_PORTFOLIO) {
          newImages.push(file);
        } else {
          extraImagesCount++;
        }
      }
    }
    setImages(newImages);

    if (extraImagesCount > 0) {
      Toast(
        'warning',
        `Máximo de ${MAX_IMAGES_PORTFOLIO} imagens por portfólio`
      );
    }
  };

  const handleAddEditImage = async (event: ChangeEvent<HTMLInputElement>) => {
    if (isEditPortfolioItem && isEditPortfolioItem.photos.length < 5) {
      const [_, error] = await handleErrorAsync(async () => {
        setIsLoading(true);
        const files = event.target.files;
        if (!files) {
          throw new Error('Nenhum arquivo selecionado');
        }
        const file = files[0];
        const formData = new FormData();
        formData.append('photo', file);
        const response = await api.post(
          `api/company_portfolio_items/${isEditPortfolioItem.id}/photos/`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
        setValuesPortfolioItem({
          ...isEditPortfolioItem,
          photos: [...isEditPortfolioItem.photos, response.data],
        });
      }).finally(() => {
        setIsLoading(false);
        resetInput(event.target);
      });
      if (error) {
        Toast('error', error.message);
        return;
      }
      Toast('success', 'Imagem adicionada com sucesso!');
    } else {
      Toast(
        'warning',
        `Máximo de ${MAX_IMAGES_PORTFOLIO} imagens por portfólio`
      );
    }
  };

  const handleCreatePortfolio = async (values: {
    title: string;
    description: string;
    pinned: string;
  }) => {
    if (!images || images.length === 0) {
      return Toast('error', 'Adicione pelo menos uma imagem ao seu portfólio');
    }
    const [_, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      const fileFormData = new FormData();

      images.forEach((image, index) => {
        fileFormData.append(`photo${index + 1}`, image);
      });
      fileFormData.append('title', values.title);
      fileFormData.append('description', values.description);
      fileFormData.append('pinned', values.pinned);

      const response = await api.post(
        `/api/companies/${companyId}/portfolio_items/`,
        fileFormData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      setValuesPortfolioItem(response.data);
      setImages([]);
    }).finally(() => setIsLoading(false));

    if (error) {
      Toast('error', error.message);
      return;
    }

    setIsOpen(false);
    Toast('success', 'Portfólio criado com sucesso!');
  };

  const handleEditPortfolio = async (values: {
    title: string;
    description: string;
    pinned: string;
  }) => {
    if (isEditPortfolioItem) {
      const [_, error] = await handleErrorAsync(async () => {
        setIsLoading(true);
        const fileFormData = new FormData();

        fileFormData.append('title', values.title);
        fileFormData.append('description', values.description);
        fileFormData.append('pinned', values.pinned);

        const response = await api.put(
          `/api/company_portfolio_items/${isEditPortfolioItem.slug}/`,
          fileFormData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );

        setValuesPortfolioItem({
          ...response.data,
          photos: isEditPortfolioItem.photos,
        });
        setImages([]);
      }).finally(() => setIsLoading(false));

      if (error) {
        Toast('error', error.message);
        return;
      }

      setIsOpen(false);
      Toast('success', 'Portfólio salvo com sucesso!');
    }
  };

  const handleDeletePortfolio = async () => {
    if (isEditPortfolioItem) {
      const [_, error] = await handleErrorAsync(async () => {
        setIsLoading(true);
        await api.delete(
          `/api/company_portfolio_items/${isEditPortfolioItem.slug}/`
        );

        if (portfolio_slug) {
          router.push(`${ROUTER_PAGES.site}/${company_slug}/portfolio/`);
          return;
        }

        setValuesPortfolioItem(undefined);
      }).finally(() => setIsLoading(false));

      if (error) {
        Toast('error', error.message);
        return;
      }

      setIsOpen(false);
      Toast('success', 'Portfólio excluído com sucesso!');
    }
  };

  const handleDeleteImage = async (photoId: number) => {
    if (isEditPortfolioItem && isEditPortfolioItem.photos.length > 1) {
      const [_, error] = await handleErrorAsync(async () => {
        setIsLoading(true);
        await api.delete(`/api/company_portfolio_item_photos/${photoId}/`);
        const photosUpdated = isEditPortfolioItem.photos.filter(
          photo => photo.id !== photoId
        );
        setValuesPortfolioItem({
          ...isEditPortfolioItem,
          photos: photosUpdated,
        });
      }).finally(() => setIsLoading(false));

      if (error) {
        Toast('error', error.message);
        return;
      }

      Toast('success', 'Imagem excluída com sucesso!');
    } else {
      Toast('error', 'Seu portfólio deve conter pelo menos uma imagem');
    }
  };

  return (
    <Modal open={isOpen}>
      <Box
        sx={theme => ({
          backgroundColor: 'primary.contrastText',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'auto',
          [theme.breakpoints.up('sm')]: {
            borderRadius: '10px',
            height: '100%',
            maxHeight: '80%',
            left: '50%',
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            maxWidth: '600px',
          },
        })}
      >
        <Box
          component="header"
          sx={{
            alignItems: 'center',
            color: 'text.primary',
            backgroundColor: 'primary.contrastText',
            display: 'grid',
            filter: 'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1))',
            gridTemplateColumns: '40px 1fr',
            paddingY: 2,
            paddingLeft: 1,
            position: 'fixed',
            top: 0,
            width: '100%',
            zIndex: 1,
          }}
        >
          <IconButton
            onClick={() => {
              setIsOpen(false);
              setImages([]);
            }}
            color="inherit"
          >
            <KeyboardBackspace />
          </IconButton>

          <Typography
            sx={{
              color: 'inherit',
              justifySelf: 'center',
              marginRight: '48px',
            }}
            variant="h6"
          >
            Portfólio
          </Typography>
        </Box>
        <Container
          sx={{
            backgroundColor: 'background.default',
            height: '100vh',
            overflow: 'auto',
            mt: '72px',
          }}
        >
          <Stack direction="column" spacing={2} sx={{ mb: 4 }}>
            <CustomForm
              initialValues={{
                title: isEditPortfolioItem ? isEditPortfolioItem.title : '',
                description: isEditPortfolioItem
                  ? isEditPortfolioItem.description
                  : '',
                pinned: isEditPortfolioItem
                  ? isEditPortfolioItem.pinned.toString()
                  : 'false',
              }}
              validationSchema={modalAddPortfolioSchema}
              onSubmit={
                isEditPortfolioItem
                  ? handleEditPortfolio
                  : handleCreatePortfolio
              }
              buttonText="salvar"
              sx={{
                gap: 2,
                my: 2,
              }}
              validateOnChange
            >
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Título *</Typography>
                <Card sx={{ p: 2, borderRadius: '12px' }}>
                  <CustomTextField
                    name="title"
                    placeholder="Digite um título"
                    fullWidth
                  />
                </Card>
              </Stack>
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Descrição *</Typography>
                <Card sx={{ p: 2, borderRadius: '12px' }}>
                  <CustomTextField
                    name="description"
                    placeholder="Digite uma descrição"
                    fullWidth
                    multiline
                  />
                </Card>
              </Stack>
              <Stack direction="column" spacing={1}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="h6">Fixar</Typography>
                  <CustomIOSSwitch
                    name="pinned"
                    data-testid="custom-IOS-switch"
                  />
                </Box>
                <Card
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#E1E1E1',
                    borderRadius: '8px',
                  }}
                >
                  <PushPinOutlined
                    fontSize="small"
                    sx={{ transform: 'rotate(45deg)', color: '#A4A4A4', mr: 2 }}
                  />

                  <Typography variant="caption">
                    Portfólios fixados serão exibidos primeiro em sua página
                    institucional!
                  </Typography>
                </Card>
              </Stack>
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Fotos</Typography>
                <Card sx={{ p: 2, borderRadius: '12px' }}>
                  <Grid container spacing={1}>
                    <Grid
                      item
                      xs={4}
                      sx={theme => ({
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        [theme.breakpoints.up('sm')]: {
                          minHeight: '176px',
                        },
                      })}
                    >
                      <IconButton
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                        }}
                        onClick={() => imagePortfolioInput.current?.click()}
                      >
                        <AddToPhotosOutlined
                          sx={{
                            transform: 'rotate(90deg)',
                            color: 'primary.main',
                          }}
                          data-testid="portfolio-img-button"
                        />
                        <Typography color="primary.main" variant="caption">
                          Adicionar
                        </Typography>
                        <input
                          className="sr-only"
                          ref={imagePortfolioInput}
                          data-testid="portfolio-img-input"
                          type="file"
                          onChange={e =>
                            isEditPortfolioItem
                              ? handleAddEditImage(e)
                              : handleAddImage(e)
                          }
                          accept={'image/png, image/jpg, image/jpeg'}
                        />
                      </IconButton>
                    </Grid>
                    {isEditPortfolioItem
                      ? isEditPortfolioItem.photos.map((image, index) => (
                          <Grid
                            item
                            xs={4}
                            key={index}
                            sx={theme => ({
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              position: 'relative',
                              cursor: 'pointer',
                              [theme.breakpoints.up('sm')]: {
                                minHeight: '176px',
                              },
                            })}
                            onClick={() => handleDeleteImage(image.id)}
                            data-testid="remove-img-button"
                          >
                            <Box className="skeleton">
                              <Box
                                component="img"
                                src={image.photo}
                                alt="Imagem adicionada"
                                sx={{
                                  objectFit: 'contain',
                                }}
                                height="100px"
                                width="100px"
                                loading="lazy"
                                onLoad={(event: {
                                  currentTarget: HTMLElement;
                                }) =>
                                  removeClassFromParent(
                                    event.currentTarget,
                                    'skeleton'
                                  )
                                }
                              />
                            </Box>
                            <IconButton
                              sx={{
                                position: 'absolute',
                                right: 0,
                                top: 10,
                                color: 'white',
                                backgroundColor: '#ff0000',
                                '&:hover': {
                                  backgroundColor: '#e50000',
                                },
                              }}
                            >
                              <DeleteForever />
                            </IconButton>
                          </Grid>
                        ))
                      : images.map((image: Blob, index: number) => (
                          <Grid
                            item
                            xs={4}
                            key={index}
                            sx={{
                              position: 'relative',
                              display: 'flex',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              setImages(images => {
                                const newArray = [...images];
                                newArray.splice(index, 1);
                                return newArray;
                              })
                            }
                            data-testid="remove-img-button"
                          >
                            <Image
                              src={URL.createObjectURL(image)}
                              alt="Imagem adicionada"
                              height="100%"
                              width="100%"
                              objectFit="contain"
                            />
                            <IconButton
                              sx={{
                                position: 'absolute',
                                right: 0,
                                top: 10,
                                color: 'white',
                                backgroundColor: '#ff0000',
                                '&:hover': {
                                  backgroundColor: '#e50000',
                                },
                              }}
                            >
                              <DeleteForever />
                            </IconButton>
                          </Grid>
                        ))}

                    {portfolioImageInAnalysis.length < MAX_IMAGES_PORTFOLIO &&
                      new Array(
                        MAX_IMAGES_PORTFOLIO - portfolioImageInAnalysis.length
                      )
                        .fill(undefined)
                        .map((_, index) => (
                          <Grid item xs={4} key={`empty-${index}`}>
                            <ImageOutlined
                              color="primary"
                              sx={{
                                width: '100%',
                                height: '100%',
                                opacity: '0.4',
                                backgroundColor: '#ededed',
                              }}
                            />
                          </Grid>
                        ))}
                  </Grid>
                </Card>
              </Stack>
              {!isEditPortfolioItem && (
                <Card
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#fbde86',
                    borderRadius: '8px',
                  }}
                >
                  <InfoOutlined sx={{ color: '#999999', mr: 2 }} />

                  <Typography variant="caption" color="#7e6f43">
                    Recomendamos fotos que tenham uma proporção de 320X320
                  </Typography>
                </Card>
              )}
            </CustomForm>
            {isEditPortfolioItem && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleDeletePortfolio()}
                sx={{ marginTop: '0px !important' }}
                data-testid="remove-portfolio-button"
              >
                excluir
              </Button>
            )}
          </Stack>
        </Container>
      </Box>
    </Modal>
  );
};

export type { IAddPortfolioProps };
